import type {
  AdminLoginRequest,
  AdminLoginResponse,
  CreateDiaryEntryRequest,
  UpdateDiaryEntryRequest,
  DiaryEntry,
} from '../types/admin.types';
import type { DiaryEntriesListResponse, DiaryEntryResponse } from '../../diary/types/diary.types';

const API_BASE = '/api';

type ErrorResponse = {
  message?: string;
};

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const parseErrorMessage = async (response: Response, defaultMessage: string): Promise<string> => {
  try {
    const errorData = (await response.json()) as ErrorResponse;
    return errorData.message ?? defaultMessage;
  } catch {
    return defaultMessage;
  }
};

export const adminLogin = async (credentials: AdminLoginRequest): Promise<AdminLoginResponse> => {
  const response = await fetch(`${API_BASE}/diary-admin/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const message = await parseErrorMessage(response, 'Nieprawidłowe dane logowania');
    throw new Error(message);
  }

  const json = (await response.json()) as AdminLoginResponse;
  return json;
};

export const adminLogout = async (): Promise<void> => {
  const response = await fetch(`${API_BASE}/diary-admin/logout`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Błąd podczas wylogowywania');
  }
};

export const fetchAdminDiaryEntries = async (): Promise<DiaryEntry[]> => {
  const response = await fetch(`${API_BASE}/diary/entries?order=desc`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Błąd podczas pobierania wpisów');
  }

  const json = (await response.json()) as DiaryEntriesListResponse;

  return json.data.map((item) => ({
    id: item.id,
    content: item.attributes.content,
    entry_date: item.attributes.entry_date,
    created_at: item.attributes.created_at,
    updated_at: item.attributes.updated_at,
  }));
};

export const createDiaryEntry = async (data: CreateDiaryEntryRequest): Promise<DiaryEntry> => {
  const response = await fetch(`${API_BASE}/diary-admin/entries`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const message = await parseErrorMessage(response, 'Błąd podczas tworzenia wpisu');
    throw new Error(message);
  }

  const json = (await response.json()) as DiaryEntryResponse;

  return {
    id: json.data.id,
    content: json.data.attributes.content,
    entry_date: json.data.attributes.entry_date,
    created_at: json.data.attributes.created_at,
    updated_at: json.data.attributes.updated_at,
  };
};

export const updateDiaryEntry = async (id: string, data: UpdateDiaryEntryRequest): Promise<DiaryEntry> => {
  const response = await fetch(`${API_BASE}/diary-admin/entries/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const message = await parseErrorMessage(response, 'Błąd podczas aktualizacji wpisu');
    throw new Error(message);
  }

  const json = (await response.json()) as DiaryEntryResponse;

  return {
    id: json.data.id,
    content: json.data.attributes.content,
    entry_date: json.data.attributes.entry_date,
    created_at: json.data.attributes.created_at,
    updated_at: json.data.attributes.updated_at,
  };
};

export const deleteDiaryEntry = async (id: string): Promise<void> => {
  const response = await fetch(`${API_BASE}/diary-admin/entries/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const message = await parseErrorMessage(response, 'Błąd podczas usuwania wpisu');
    throw new Error(message);
  }
};
